import {PropTypes} from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Notary} from '../classes/Notary';
import {Notification} from '../classes/Notification';
import {Person} from '../classes/Person';
import locale from '../locale/Localization';
import {addApplication} from '../script/application-service';
import {getPersonById, updatePerson} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import cleanseNotaryData from '../utils/cleanseNotaryFormData';
import {
  NOTARY_ADD_APPLICATION,
  NOTARY_EDIT_DETAILS,
  NOTARY_TABLE,
  NOTARY_VIEW_DETAILS
} from '../utils/constants';
import NotariesList from './NotariesList';
import NotaryAdd from './NotaryAdd';
import NotaryDetails from './NotaryDetails';
import NotaryEdit from './NotaryEdit';

/**
 * Map the properties.x
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    addNotifications: (notification) => dispatch(addNotifications(notification))
  };
}

/**
 * Connected component class of NotariesApp.
 */
class ConnectedNotariesApp extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    super(props);
    this.state = {
      notaryDetails: null,
      notaryDetailsShown: false,
      notaryAddShown: false,
      notaryEditShown: false,
      personId: null,
      viewMode: NOTARY_TABLE
    };
  }

  /**
   * Changes the components displayed by opening the notary details.
   *
   * @param {Object} notaryData - The object containing notary details.
   */
  notaryDetailsToggle = async (notaryData: Object) => {
    if (this.state.viewMode === NOTARY_TABLE) {
      await this.fetchNotaryDetails(notaryData.id, NOTARY_VIEW_DETAILS);
      await this.setState({
        viewMode: NOTARY_VIEW_DETAILS
      });
    } else {
      await this.setState({
        viewMode: NOTARY_TABLE
      });
    }
  };

  /**
   * Changes the components displayed by opening the notary add form.
   */
  notaryAddToggle = () => {
    if (this.state.viewMode === NOTARY_TABLE) {
      this.setState({
        viewMode: NOTARY_ADD_APPLICATION
      });
    } else {
      this.setState({
        viewMode: NOTARY_TABLE
      });
    }
  };

  /**
   * Handles the submit of the notary add button and notary application.
   *
   * @param {Object} applicationData - The notary application data from submit action.
   * @param {number} stateNotaryId - The state notary id.
   */
  handleNotaryAddSubmit = (applicationData: Object, stateNotaryId: number) => {
    if (applicationData) {
      cleanseNotaryData(applicationData);
      applicationData.sendMail = false;
      applicationData.stateNotaryId = stateNotaryId;

      addApplication(applicationData).then((response) => {
        if (response.created) {
          this.notaryAddToggle();
          this.props.addNotifications([
            new Notification(
              locale.submitApplicationCompletedNotification,
              'success',
              6000
            )
          ]);
        } else {
          this.props.addNotifications([
            new Notification(
              locale.submitApplicationErrorNotification,
              'error',
              6000
            )
          ]);
        }
      });
    }
  };

  /**
   * Changes the components displayed by opening the notary edit form.
   *
   * @param {Object} notaryData - The object containing notary details obtained from row.
   */
  notaryEditToggle = async (notaryData: Object) => {
    if (this.state.viewMode === NOTARY_TABLE) {
      await this.fetchNotaryDetails(notaryData.id, NOTARY_EDIT_DETAILS);
      this.setState({
        viewMode: NOTARY_EDIT_DETAILS
      });
    } else {
      await this.setState({
        viewMode: NOTARY_TABLE
      });
    }
  };

  /**
   * Fetch the detail information of the notary.
   *
   * @param {number} notaryId - The id of the notary.
   * @param {string} viewMode - The mode of the view.
   */
  fetchNotaryDetails = async (notaryId: number, viewMode: string) => {
    let notaryData = {};

    await getPersonById(notaryId).then((result) => {
      if (viewMode === NOTARY_EDIT_DETAILS) {
        const organization = result.organisation || {};
        const businessContactInfo = organization.contactInformation || {};

        notaryData = new Notary(
          result.gender,
          result.firstNames,
          result.officialName,
          result.birthDate,
          null,
          organization.uid,
          organization.name,
          businessContactInfo.street,
          businessContactInfo.houseNumber,
          businessContactInfo.addition,
          businessContactInfo.postOffice,
          businessContactInfo.swissZip,
          businessContactInfo.town,
          businessContactInfo.careOf,
          businessContactInfo.mail,
          businessContactInfo.telephone,
          businessContactInfo.fax,
          result.certificate,
          null,
          null,
          result.functions,
          null,
          result.signatureDeclarationDocument,
          result.registrationDocument,
          result.terminationDocument,
          null,
          null,
          null,
          null,
          null,
          result.contactInformation?.mobileNumber
        );
      } else if (viewMode === NOTARY_VIEW_DETAILS) {
        notaryData = result;
      }
    });
    await this.setState({notaryDetails: notaryData, personId: notaryId});
  };

  /**
   * Handles the submit of the notary edit button and updates notary detail information.
   *
   * @param {Object} personData - Person data from submit action.
   */
  handleNotaryEditSubmit = (personData: Object) => {
    if (this.state.personId && personData) {
      const person = new Person(
        this.state.personId,
        personData.name,
        personData.firstName,
        personData.birthDate,
        personData.gender,
        personData.company,
        personData.uid,
        personData.street,
        personData.streetNumber,
        personData.addition,
        personData.postOffice,
        personData.postcode,
        personData.location,
        personData.careOf,
        personData.faxNumber,
        personData.email,
        personData.phoneNumber,
        personData.mobileNumber
      );

      updatePerson(person, this.state.personId).then((response) => {
        if (response.updated) {
          this.props.addNotifications([
            new Notification(
              locale.editPersonCompletedNotification,
              'success',
              6000
            )
          ]);
        } else {
          this.props.addNotifications([
            new Notification(locale.editPersonErrorNotification, 'error', 6000)
          ]);
        }
      });
    }
  };

  /**
   * Renders NotariesApp component.
   *
   * @return {string} - The notary application component to be rendered.
   */
  // eslint-disable-next-line max-lines-per-function, require-jsdoc
  render() {
    return (
      <div id='notaries'>
        <div id='notaries-list' hidden={this.state.viewMode !== NOTARY_TABLE}>
          <NotariesList
            onNotaryAddShown={this.notaryAddToggle}
            onNotaryDetailsShown={this.notaryDetailsToggle}
            onNotaryEditShown={this.notaryEditToggle}
          />
        </div>
        <div id='notary-details'>
          {this.state.viewMode === NOTARY_VIEW_DETAILS && (
            <NotaryDetails
              viewMode={this.state.viewMode}
              notaryDetails={this.state.notaryDetails}
              onBackLinkClick={this.notaryDetailsToggle}
            />
          )}
        </div>
        <div id='notary-add'>
          {this.state.viewMode === NOTARY_ADD_APPLICATION && (
            <NotaryAdd
              viewMode={this.state.viewMode}
              handleSubmit={this.handleNotaryAddSubmit}
              onBackLinkClick={this.notaryAddToggle}
            />
          )}
        </div>
        <div id='notary-edit'>
          {this.state.viewMode === NOTARY_EDIT_DETAILS && (
            <NotaryEdit
              viewMode={this.state.viewMode}
              handleSubmit={this.handleNotaryEditSubmit}
              notaryDetails={this.state.notaryDetails}
              personId={this.state.personId}
              onBackLinkClick={this.notaryEditToggle}
            />
          )}
        </div>
      </div>
    );
  }
}

ConnectedNotariesApp.propTypes = {
  addNotifications: PropTypes.func.isRequired
};

const NotariesApp = connect(null, mapDispatchToProps)(ConnectedNotariesApp);
export default NotariesApp;
